import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";
import { Section, Overline } from "@/components/site/Section";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Direct Placement", "RPO Solutions", "HR Services", "Executive Search", "Not Sure Yet",
];
const INDUSTRY_OPTIONS = [
  "Technology & SaaS", "Healthcare", "Aerospace & Aviation",
  "Engineering & Manufacturing", "Finance & Accounting", "Legal",
  "Human Resources", "Other",
];
const EMPTY_FORM = {
  name: "", email: "", company: "", phone: "",
  service_interest: "", industry: "", message: "",
};

function useLeadForm(onSuccess) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/leads`, payload);
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm(EMPTY_FORM);
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return { form, setField, submitting, handleSubmit };
}

function ContactHero() {
  return (
    <Section className="pb-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <Overline className="mb-5">Get in touch</Overline>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05]">
            Let's build your <span className="italic text-[hsl(var(--primary))]">workforce</span> together.
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-muted-foreground">
            Whether you need direct placement support, RPO solutions, or HR services — tell us
            about your hiring goals and we'll respond within one business day.
          </p>
        </div>
      </div>
    </Section>
  );
}

function ContactInfo() {
  return (
    <div className="lg:col-span-4 space-y-6">
      <div className="bg-[hsl(var(--secondary))]/50 border border-border rounded-xl p-7">
        <Overline className="mb-3">Direct contact</Overline>
        <ul className="space-y-3 text-sm">
          <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))]" /><a href="mailto:robert@x-hrverse.com" className="hover:underline">robert@x-hrverse.com</a></li>
          <li className="flex items-start gap-3"><Phone className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))]" /><span>+1 (555) 010-2030</span></li>
          <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))]" /><span>United States · Remote-first</span></li>
        </ul>
      </div>
      <div className="bg-[hsl(var(--background))] border border-border rounded-xl p-7">
        <Overline className="mb-3">What happens next</Overline>
        <ol className="space-y-3 text-sm text-muted-foreground">
          <li className="flex gap-3"><span className="font-display text-[hsl(var(--accent))]">01</span> We review your request within 1 business day.</li>
          <li className="flex gap-3"><span className="font-display text-[hsl(var(--accent))]">02</span> A 30-min discovery call to understand your goals.</li>
          <li className="flex gap-3"><span className="font-display text-[hsl(var(--accent))]">03</span> A tailored engagement plan — no obligation.</li>
        </ol>
      </div>
    </div>
  );
}

function SuccessCard({ onReset }) {
  return (
    <div data-testid="contact-success" className="bg-[hsl(var(--primary))] text-white rounded-xl p-10 md:p-14 border border-border">
      <CheckCircle2 className="h-10 w-10" />
      <h2 className="mt-5 font-display text-3xl md:text-4xl">Thanks — message received.</h2>
      <p className="mt-3 text-white/80 max-w-xl">A member of our team will respond within one business day. In the meantime, feel free to explore our services and industries.</p>
      <button
        onClick={onReset}
        data-testid="contact-send-another"
        className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[hsl(var(--accent))] text-white text-sm font-medium hover:bg-[#c84d2e]"
      >
        Send another message <ArrowUpRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function LeadForm({ onSuccess }) {
  const { form, setField, submitting, handleSubmit } = useLeadForm(onSuccess);
  return (
    <form
      onSubmit={handleSubmit}
      data-testid="contact-form"
      className="bg-[hsl(var(--background))] border border-border rounded-xl p-7 md:p-10 space-y-5"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name *" testid="contact-name" value={form.name} onChange={setField("name")} required />
        <Field label="Work email *" type="email" testid="contact-email" value={form.email} onChange={setField("email")} required />
        <Field label="Company" testid="contact-company" value={form.company} onChange={setField("company")} />
        <Field label="Phone" testid="contact-phone" value={form.phone} onChange={setField("phone")} />
        <Select label="Service of interest" testid="contact-service" value={form.service_interest} onChange={setField("service_interest")} options={SERVICE_OPTIONS} />
        <Select label="Industry" testid="contact-industry" value={form.industry} onChange={setField("industry")} options={INDUSTRY_OPTIONS} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">How can we help? *</label>
        <textarea
          required
          rows={5}
          data-testid="contact-message"
          value={form.message}
          onChange={setField("message")}
          className="w-full bg-transparent border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:border-[hsl(var(--ring))]"
          placeholder="Roles you need to fill, timeline, hiring volume, current challenges..."
        />
      </div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-muted-foreground">We respond within one business day.</p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[#1A3A31] disabled:opacity-60"
        >
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Send message <ArrowUpRight className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
}

export default function Contact() {
  const [done, setDone] = useState(false);

  return (
    <div data-testid="page-contact">
      <ContactHero />
      <Section className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <ContactInfo />
          <div className="lg:col-span-8">
            {done ? (
              <SuccessCard onReset={() => setDone(false)} />
            ) : (
              <LeadForm onSuccess={() => setDone(true)} />
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <input
        type={type}
        required={required}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:border-[hsl(var(--ring))]"
      />
    </div>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:border-[hsl(var(--ring))]"
      >
        <option value="">Select an option</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
